// src/components/settings/EmailSettings.js
import React, { useState, useEffect } from 'react';
import { settingsService } from '../../services/settingsService';

const EmailSettings = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [processingQueue, setProcessingQueue] = useState(false);
    const [pendingQueueCount, setPendingQueueCount] = useState(0);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [settings, setSettings] = useState({
        smtp_host: '',
        smtp_port: '',
        smtp_username: '',
        smtp_password: '',
        from_email: '',
        from_name: '',
    });

    useEffect(() => {
        fetchSettings();
        fetchQueueStats();
    }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const response = await settingsService.getSystemSettings();
            const emailSettings = {};

            // Extract email-related settings
            // response.data contains the actual data from API
            const data = response.data || response;
            data.grouped?.email?.forEach(setting => {
                emailSettings[setting.setting_key] = setting.setting_value;
            });

            setSettings(prevSettings => ({ ...prevSettings, ...emailSettings }));
        } catch (error) {
            showMessage('error', 'Failed to load email settings');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setSettings(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setSaving(true);
        try {
            // Convert settings object to array format expected by API
            const settingsArray = Object.entries(settings).map(([key, value]) => ({
                setting_key: key,
                setting_value: value
            }));

            await settingsService.updateSystemSettings(settingsArray);
            showMessage('success', 'Email settings updated successfully');
        } catch (error) {
            showMessage('error', error.message || 'Failed to update settings');
        } finally {
            setSaving(false);
        }
    };

    const showMessage = (type, text) => {
        setMessage({ type, text });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    };

    const handleProcessQueue = async () => {
        setProcessingQueue(true);
        try {
            const response = await settingsService.processPendingEmailQueue(200);
            const data = response.data || response;
            showMessage(
                'success',
                `Queue run complete: ${data.sent || 0} sent, ${data.failed || 0} failed, ${data.pending_found || 0} processed.`
            );
            await fetchQueueStats();
        } catch (error) {
            showMessage('error', error?.response?.data?.message || 'Failed to process pending email queue');
        } finally {
            setProcessingQueue(false);
        }
    };

    const fetchQueueStats = async () => {
        try {
            const response = await settingsService.getEmailQueueStats();
            const data = response.data || response;
            setPendingQueueCount(Number(data.pending_ready || 0));
        } catch (error) {
            setPendingQueueCount(0);
        }
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner-large"></div>
                <p>Loading email settings...</p>
            </div>
        );
    }

    return (
        <div className="email-settings">
            <h2 className="section-title">
                <i className="fas fa-envelope-open-text"></i>
                Email Configuration
            </h2>

            {message.text && (
                <div className={`message ${message.type}`}>
                    <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
                    {message.text}
                </div>
            )}

            <form onSubmit={handleSubmit} className="settings-form">
                <div className="form-section">
                    <h3 className="form-section-title">SMTP Server Settings</h3>

                    <div className="form-group">
                        <label htmlFor="smtp_host">
                            SMTP Host <span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            id="smtp_host"
                            name="smtp_host"
                            value={settings.smtp_host}
                            onChange={handleChange}
                            placeholder="e.g., smtp.gmail.com"
                            required
                        />
                        <small>The SMTP server address</small>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="smtp_port">
                                SMTP Port <span className="required">*</span>
                            </label>
                            <input
                                type="number"
                                id="smtp_port"
                                name="smtp_port"
                                value={settings.smtp_port}
                                onChange={handleChange}
                                placeholder="465 or 587"
                                required
                            />
                            <small>Common ports: 465 (SSL) or 587 (TLS)</small>
                        </div>

                        <div className="form-group">
                            <label htmlFor="smtp_username">
                                SMTP Username <span className="required">*</span>
                            </label>
                            <input
                                type="text"
                                id="smtp_username"
                                name="smtp_username"
                                value={settings.smtp_username}
                                onChange={handleChange}
                                placeholder="Email address or username"
                                required
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="smtp_password">
                            SMTP Password <span className="required">*</span>
                        </label>
                        <input
                            type="password"
                            id="smtp_password"
                            name="smtp_password"
                            value={settings.smtp_password}
                            onChange={handleChange}
                            placeholder="••••••••"
                            required
                        />
                        <small>Your SMTP password or app-specific password</small>
                    </div>
                </div>

                <div className="form-section">
                    <h3 className="form-section-title">Email Sender Information</h3>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="from_email">
                                From Email <span className="required">*</span>
                            </label>
                            <input
                                type="email"
                                id="from_email"
                                name="from_email"
                                value={settings.from_email}
                                onChange={handleChange}
                                placeholder="noreply@ehaazri.com"
                                required
                            />
                            <small>Email address shown as sender</small>
                        </div>

                        <div className="form-group">
                            <label htmlFor="from_name">
                                From Name <span className="required">*</span>
                            </label>
                            <input
                                type="text"
                                id="from_name"
                                name="from_name"
                                value={settings.from_name}
                                onChange={handleChange}
                                placeholder="eHAAZRI"
                                required
                            />
                            <small>Name shown as sender</small>
                        </div>
                    </div>
                </div>

                <div className="form-actions">
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                        {saving ? (
                            <>
                                <div className="spinner"></div>
                                Saving...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-save"></i>
                                Save Email Settings
                            </>
                        )}
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleProcessQueue}
                        disabled={processingQueue}
                    >
                        {processingQueue ? (
                            <>
                                <div className="spinner"></div>
                                Running Queue...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-paper-plane"></i>
                                Run Pending Email Queue ({pendingQueueCount})
                            </>
                        )}
                    </button>
                </div>
            </form>

            <style jsx>{`
                .email-settings {
                    animation: fadeIn 0.3s ease-in;
                }

                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                .loading-container {
                    text-align: center;
                    padding: 4rem 0;
                }

                .spinner-large {
                    width: 50px;
                    height: 50px;
                    border: 4px solid #f3f4f6;
                    border-top: 4px solid #667eea;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    margin: 0 auto 1rem;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                .section-title {
                    font-size: 1.5rem;
                    font-weight: 700;
                    margin-bottom: 2rem;
                    color: #2d3748;
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                }

                .section-title i {
                    color: #667eea;
                }

                .message {
                    padding: 1rem 1.25rem;
                    border-radius: 10px;
                    margin-bottom: 1.5rem;
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    font-weight: 500;
                    animation: slideDown 0.3s ease-out;
                }

                .message.success {
                    background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
                    color: #155724;
                    border-left: 4px solid #28a745;
                }

                .message.error {
                    background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
                    color: #721c24;
                    border-left: 4px solid #dc3545;
                }

                .settings-form {
                    max-width: 800px;
                }

                .form-section {
                    background: #f8f9fa;
                    border-radius: 12px;
                    padding: 2rem;
                    margin-bottom: 2rem;
                    border: 2px solid #e9ecef;
                }

                .form-section-title {
                    font-size: 1.1rem;
                    font-weight: 600;
                    margin: 0 0 1.5rem 0;
                    color: #495057;
                    padding-bottom: 0.75rem;
                    border-bottom: 2px solid #dee2e6;
                }

                .form-row {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 1.5rem;
                }

                .form-group {
                    margin-bottom: 1.5rem;
                }

                .form-group label {
                    display: block;
                    font-weight: 600;
                    margin-bottom: 0.5rem;
                    color: #2d3748;
                }

                .required {
                    color: #dc3545;
                }

                .form-group input {
                    width: 100%;
                    padding: 0.75rem 1rem;
                    border: 2px solid #dee2e6;
                    border-radius: 8px;
                    font-size: 1rem;
                    transition: all 0.3s ease;
                    background: white;
                }

                .form-group input:focus {
                    outline: none;
                    border-color: #667eea;
                    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
                }

                .form-group small {
                    display: block;
                    margin-top: 0.5rem;
                    color: #6c757d;
                    font-size: 0.875rem;
                }

                .form-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: 1rem;
                    padding-top: 1rem;
                }

                .spinner {
                    width: 1rem;
                    height: 1rem;
                    border: 2px solid rgba(255, 255, 255, 0.3);
                    border-top: 2px solid white;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    margin-right: 0.5rem;
                }

                .btn.btn-secondary {
                    background: #1f2937;
                    color: #fff;
                    border: none;
                }

                .btn.btn-secondary:hover {
                    background: #111827;
                }

                @media (max-width: 768px) {
                    .form-row {
                        grid-template-columns: 1fr;
                    }

                    .form-section {
                        padding: 1.5rem;
                    }
                }
            `}</style>
        </div>
    );
};

export default EmailSettings;
